import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import {
  customFieldResourceTypeLabels,
  removeDataExtractionFieldNamePrefix,
} from "@repo/app-types/entities/customField";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { toHumanReadableString } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { type FC, useState } from "react";
import { useUpdateDataExtractionFieldDefinitionMutation } from "../../../api/entities/dataExtractionField.js";

interface EditDataExtractionFieldDefinitionFormProps {
  field: CustomFieldDefinition;
  onClose: () => void;
}

export const EditDataExtractionFieldDefinitionForm: FC<
  EditDataExtractionFieldDefinitionFormProps
> = ({ field, onClose }) => {
  const [updateField, { isLoading: isSaving }] =
    useUpdateDataExtractionFieldDefinitionMutation();

  const [displayName, setDisplayName] = useState(field.display_name);
  const [description, setDescription] = useState(
    field.settings?.description ?? "",
  );

  const hasChanged =
    displayName.trim() !== field.display_name ||
    description.trim() !== (field.settings?.description ?? "");

  const onSave = async () => {
    if (!displayName.trim()) return;

    try {
      await updateField({
        id: field.id,
        display_name: displayName.trim(),
        description: description.trim() || null,
      }).unwrap();

      showToast({
        title: "Field updated successfully",
        variant: ToastVariant.Success,
      });

      onClose();
    } catch {
      showToast({
        title: "Failed to update field",
        variant: ToastVariant.Error,
      });
    }
  };

  return (
    <div className="mt-4 flex w-[560px] flex-col space-y-4">
      <Input
        label="Name"
        id="edit_display_name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
      />

      <Textarea
        label="Description"
        id="edit_description"
        value={description}
        onChange={setDescription}
        description="Optional. Extra context and instructions that guide the extraction and document what this field means."
        textareaClassName="min-h-12"
        maxLength={5000}
      />

      <Input
        label="Key"
        id="edit_field_name"
        value={removeDataExtractionFieldNamePrefix(field)}
        onChange={() => {}}
        isDisabled
        maxLength={100}
      />

      <Input
        label="Type"
        id="edit_field_type"
        value={toHumanReadableString(field.field_type)}
        onChange={() => {}}
        isDisabled
        maxLength={100}
      />

      {field.resource_type in customFieldResourceTypeLabels ? (
        <Input
          label="Resource"
          id="edit_resource_type"
          value={customFieldResourceTypeLabels[field.resource_type]}
          onChange={() => {}}
          isDisabled
          maxLength={100}
        />
      ) : null}

      <div className="flex justify-end space-x-2">
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          isLoading={isSaving}
          isDisabled={!displayName.trim() || !hasChanged}
          onClick={onSave}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
};
